import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import gsap from "gsap";
import { useLeftSidebar } from "@/components/ui/sidebar";
import {
  clampDockWidth,
  DOCK_DEFAULT_WIDTH,
  dockViewportMax,
} from "../constants";
import { useChatDockStore } from "../store/chat-dock-store";

/**
 * Desktop dock kolonunu sol kenarından tut-sürükle ile yeniden boyutlar.
 * Genişlik store'da persist edilir; GSAP açılış/kapanış hedefi bu değerdir.
 */
export const useDockResize = (
  asideRef: RefObject<HTMLElement | null>,
  enabled: boolean,
) => {
  const width = useChatDockStore((s) => s.width);
  const setWidth = useChatDockStore((s) => s.setWidth);
  const { open: isLeftSidebarOpen } = useLeftSidebar();

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const widthRef = useRef(width);
  widthRef.current = width;

  const applyWidth = useCallback(
    (next: number) => {
      const max = dockViewportMax(window.innerWidth, isLeftSidebarOpen);
      const clamped = clampDockWidth(next, max);
      setWidth(clamped);
      const aside = asideRef.current;
      if (enabled && aside) gsap.set(aside, { width: clamped });
      return clamped;
    },
    [asideRef, enabled, isLeftSidebarOpen, setWidth],
  );

  // Viewport / sol sidebar değişince mevcut genişliği yeni tavana sıkıştır
  useEffect(() => {
    const constrain = () => {
      const max = dockViewportMax(window.innerWidth, isLeftSidebarOpen);
      if (widthRef.current > max) applyWidth(max);
    };
    constrain();
    window.addEventListener("resize", constrain);
    return () => window.removeEventListener("resize", constrain);
  }, [applyWidth, isLeftSidebarOpen]);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Sol kenar: sola çekmek paneli genişletir
      applyWidth(drag.startWidth + (drag.startX - e.clientX));
    };
    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [applyWidth, isDragging]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: widthRef.current };
      setIsDragging(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [],
  );

  const onDoubleClick = useCallback(() => {
    applyWidth(DOCK_DEFAULT_WIDTH);
  }, [applyWidth]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      const step = e.shiftKey ? 48 : 16;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        applyWidth(widthRef.current + step);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        applyWidth(widthRef.current - step);
      } else if (e.key === "Home") {
        e.preventDefault();
        applyWidth(DOCK_DEFAULT_WIDTH);
      }
    },
    [applyWidth],
  );

  return {
    width,
    isDragging,
    handleProps: {
      onPointerDown,
      onDoubleClick,
      onKeyDown,
    },
  };
};
